import React, { useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { TextFieldProps } from "@mui/material/TextField/TextField";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import FormikSelect, { TOption } from "./FormikSelect";
import FormikTextField from "./FormikTextField";
import { MINUTES, HOURS, DAYS, WEEKS } from "./Constants";
import DialogButtons from "./DialogButtons";
import { useTranslation } from "react-i18next";

export type TTimestampUnit =
  | typeof MINUTES
  | typeof HOURS
  | typeof DAYS
  | typeof WEEKS;

const unitOptions: TOption<string>[] = [
  { value: MINUTES },
  { value: HOURS },
  { value: DAYS },
  { value: WEEKS },
];

interface ITimestampConditionDialogProps {
  title: string;
  value: number;
  unit: TTimestampUnit;
  open: boolean;
  onClose: (value: number, unit: TTimestampUnit) => void;
}

const TimestampConditionDialog = (props: ITimestampConditionDialogProps) => {
  const { title, value, unit, open, onClose } = props;
  const { t } = useTranslation();

  return (
    <Dialog open={open} fullWidth data-cy="condition-picker-condition-dialog">
      <DialogTitle>{title}</DialogTitle>
      <Formik
        initialValues={{ value: value, unit: unit }}
        validationSchema={Yup.object({
          value: Yup.number().required(t("A number is required")),
        })}
        onSubmit={(values) => onClose(values.value, values.unit)}
      >
        <Form>
          <DialogContent>
            <Box gap={1} display="flex" flexDirection="row">
              <FormikTextField
                label={t("Value")}
                name="value"
                type="number"
                style={{ width: "60%" }}
                margin="none"
                data-cy="condition-picker-value"
              />
              <FormikSelect
                label={t("Unit")}
                name="unit"
                options={unitOptions}
                style={{ width: "40%" }}
                margin="none"
                data-cy="timestamp-picker-select-unit"
              />
            </Box>
          </DialogContent>
          <DialogButtons
            submitButtonLabel="Save"
            resetCallback={() => onClose(value, unit)}
          />
        </Form>
      </Formik>
    </Dialog>
  );
};

type TTimestampConditionPickerProps = {
  label: string;
  value: number;
  unit: TTimestampUnit;
  onConditionChange: (value: number, unit: TTimestampUnit) => void;
};

const TimestampConditionPicker = (
  props: TTimestampConditionPickerProps & TextFieldProps
) => {
  const { label, value, unit, onConditionChange, ...textFieldProps } = props;
  const [condition, setCondition] = useState<string>(value + " " + unit);
  const [open, setOpen] = useState<boolean>(false);

  const handleClick = () => {
    setOpen(true);
  };

  const handleClose = (value: number, unit: TTimestampUnit) => {
    setOpen(false);
    setCondition(value.toString() + " " + unit);
    onConditionChange(value, unit);
  };

  return (
    <>
      <TextField
        {...textFieldProps}
        label={label}
        value={condition}
        InputProps={{
          readOnly: true,
        }}
        onClick={handleClick}
      />
      <TimestampConditionDialog
        title={label}
        value={value}
        unit={unit}
        open={open}
        onClose={handleClose}
      />
    </>
  );
};

export default TimestampConditionPicker;
